/** Couleurs choisies par l'école. Les autres teintes (texte, bordures, survols) en découlent. */
export type AppPalette = {
  page: string;
  surface: string;
  sidebar: string;
  sidebarText: string;
  sidebarActive: string;
  accent: string;
  header: string;
};

export type ThemeIssue =
  | "appearance.invalid"
  | "appearance.contrast_sidebar"
  | "appearance.contrast_surfaces"
  | "appearance.contrast_active"
  | "appearance.contrast_accent";

export const DEFAULT_PALETTE: AppPalette = {
  page: "#efece6",
  surface: "#fbfaf7",
  sidebar: "#0b1220",
  sidebarText: "#f5f7fb",
  sidebarActive: "#373d48",
  accent: "#2f5bff",
  header: "#efece6",
};

export const PRESETS: ReadonlyArray<{ id: string; labelKey: string; palette: AppPalette }> = [
  { id: "scola", labelKey: "appearance.preset_scola", palette: DEFAULT_PALETTE },
  {
    id: "ardoise",
    labelKey: "appearance.preset_ardoise",
    palette: {
      page: "#e7e9ee",
      surface: "#f7f8fa",
      sidebar: "#1c2430",
      sidebarText: "#f4f6f8",
      sidebarActive: "#454b55",
      accent: "#1d4ed8",
      header: "#f4f6f8",
    },
  },
  {
    id: "foret",
    labelKey: "appearance.preset_foret",
    palette: {
      page: "#e8efe8",
      surface: "#f6faf6",
      sidebar: "#14241c",
      sidebarText: "#f3f7f4",
      sidebarActive: "#3e4b45",
      accent: "#0f766e",
      header: "#f3f7f4",
    },
  },
  {
    id: "encre",
    labelKey: "appearance.preset_encre",
    palette: {
      page: "#f3f1ec",
      surface: "#fbfaf7",
      sidebar: "#161616",
      sidebarText: "#f5f5f4",
      sidebarActive: "#404040",
      accent: "#1f2937",
      header: "#fafaf8",
    },
  },
  {
    id: "sable",
    labelKey: "appearance.preset_sable",
    palette: {
      page: "#f6f1e8",
      surface: "#fbf8f3",
      sidebar: "#3a2a1c",
      sidebarText: "#f7f3ee",
      sidebarActive: "#5d5045",
      accent: "#8a5a2b",
      header: "#fbf8f3",
    },
  },
];

const TEXT_CONTRAST = 4.5;
const ACTIVE_CONTRAST = 1.5;
/** Le cobalt livré (#2f5bff sur #efece6) est à 4,39 : on le garde. */
const ACCENT_CONTRAST = 4.3;
const INK = "#0b1220";
const PAPER = "#f5f7fb";

export const SURFACE_VARS = [
  "--bg-base",
  "--bg-surface",
  "--bg-elevated",
  "--bg-inset",
  "--fg-strong",
  "--fg-default",
  "--fg-muted",
  "--fg-subtle",
  "--border-default",
  "--border-strong",
  "--focus-offset",
] as const;

export const SIDEBAR_VARS = [
  "--sidebar-bg",
  "--sidebar-bg-top",
  "--sidebar-bg-deep",
  "--sidebar-fg",
  "--sidebar-fg-muted",
  "--sidebar-fg-subtle",
  "--sidebar-line",
  "--sidebar-active",
  "--sidebar-active-fg",
  "--sidebar-hover",
] as const;

export const ACCENT_VARS = [
  "--accent-base",
  "--accent-hover",
  "--accent-pressed",
  "--accent-soft",
  "--accent-alt",
  "--accent-alt-hover",
  "--accent-alt-soft",
  "--accent-glow",
  "--accent-fg",
  "--focus-ring",
] as const;

export const HEADER_VARS = [
  "--bg-header",
  "--header-fg",
  "--header-fg-muted",
  "--header-border",
] as const;

export const THEME_VAR_NAMES = [
  ...SURFACE_VARS,
  ...SIDEBAR_VARS,
  ...ACCENT_VARS,
  ...HEADER_VARS,
] as const;

type Rgb = { r: number; g: number; b: number };

export function normalizeHex(input: string): string | null {
  const raw = input.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw
      .split("")
      .map((channel) => channel + channel)
      .join("")
      .toLowerCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  return null;
}

export function palettesEqual(a: AppPalette, b: AppPalette): boolean {
  return (Object.keys(DEFAULT_PALETTE) as Array<keyof AppPalette>).every(
    (key) => a[key] === b[key]
  );
}

export function isDefaultPalette(palette: AppPalette): boolean {
  return palettesEqual(palette, DEFAULT_PALETTE);
}

function rgb(hex: string): Rgb {
  const value = Number.parseInt(hex.slice(1), 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function mix(from: string, to: string, amount: number): string {
  const start = rgb(from);
  const end = rgb(to);
  return toHex({
    r: start.r + (end.r - start.r) * amount,
    g: start.g + (end.g - start.g) * amount,
    b: start.b + (end.b - start.b) * amount,
  });
}

function channelLuminance(channel: number): number {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

export function contrast(foreground: string, background: string): number {
  const a = rgb(foreground);
  const b = rgb(background);
  const lum = (color: Rgb) =>
    0.2126 * channelLuminance(color.r) +
    0.7152 * channelLuminance(color.g) +
    0.0722 * channelLuminance(color.b);
  const lighter = Math.max(lum(a), lum(b));
  const darker = Math.min(lum(a), lum(b));
  return (lighter + 0.05) / (darker + 0.05);
}

function bestInk(background: string): string {
  return contrast(INK, background) >= contrast(PAPER, background) ? INK : PAPER;
}

function toneToward(ink: string, background: string, minContrast: number): string {
  let chosen = ink;
  for (let step = 0; step <= 36; step += 1) {
    const next = mix(ink, background, step / 36);
    if (contrast(next, background) < minContrast) break;
    chosen = next;
  }
  return chosen;
}

function sharedInk(backgrounds: string[]): string | null {
  const darkOk = backgrounds.every((background) => contrast(INK, background) >= TEXT_CONTRAST);
  const lightOk = backgrounds.every((background) => contrast(PAPER, background) >= TEXT_CONTRAST);
  if (darkOk && !lightOk) return INK;
  if (lightOk && !darkOk) return PAPER;
  if (darkOk && lightOk) {
    const darkScore = backgrounds.reduce((sum, background) => sum + contrast(INK, background), 0);
    const lightScore = backgrounds.reduce(
      (sum, background) => sum + contrast(PAPER, background),
      0
    );
    return darkScore >= lightScore ? INK : PAPER;
  }
  return null;
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = rgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function validatePalette(palette: AppPalette): ThemeIssue | null {
  const colors = Object.values(palette);
  if (colors.some((color) => normalizeHex(color) !== color)) return "appearance.invalid";
  if (!sharedInk([palette.page, palette.surface])) return "appearance.contrast_surfaces";
  if (contrast(palette.sidebarText, palette.sidebar) < TEXT_CONTRAST) {
    return "appearance.contrast_sidebar";
  }
  if (contrast(palette.sidebarActive, palette.sidebar) < ACTIVE_CONTRAST) {
    return "appearance.contrast_active";
  }
  const activeInk =
    contrast(palette.sidebarText, palette.sidebarActive) >= TEXT_CONTRAST
      ? palette.sidebarText
      : bestInk(palette.sidebarActive);
  if (contrast(activeInk, palette.sidebarActive) < TEXT_CONTRAST)
    return "appearance.contrast_active";
  const buttonInk = bestInk(palette.accent);
  const accentOnPage = contrast(palette.accent, palette.page) >= ACCENT_CONTRAST;
  const accentOnSurface = contrast(palette.accent, palette.surface) >= ACCENT_CONTRAST;
  if (contrast(buttonInk, palette.accent) < TEXT_CONTRAST || !accentOnPage || !accentOnSurface) {
    return "appearance.contrast_accent";
  }
  if (!sharedInk([palette.header])) return "appearance.contrast_surfaces";
  return null;
}

function surfaceVariables(palette: AppPalette): Record<string, string> {
  const ink = sharedInk([palette.page, palette.surface]) ?? INK;
  const hardest = [palette.page, palette.surface].reduce((worst, background) =>
    contrast(ink, background) < contrast(ink, worst) ? background : worst
  );
  return {
    "--bg-base": palette.page,
    "--bg-surface": palette.surface,
    "--bg-elevated": mix(palette.surface, palette.page, 0.45),
    "--bg-inset": mix(palette.page, ink, 0.06),
    "--fg-strong": ink,
    "--fg-default": toneToward(ink, hardest, 7),
    "--fg-muted": toneToward(ink, hardest, TEXT_CONTRAST),
    "--fg-subtle": toneToward(ink, hardest, 3),
    "--border-default": mix(palette.page, ink, 0.14),
    "--border-strong": mix(palette.page, ink, 0.24),
    "--focus-offset": palette.page,
  };
}

function sidebarVariables(palette: AppPalette): Record<string, string> {
  const sidebarIsLight = contrast(INK, palette.sidebar) >= contrast(PAPER, palette.sidebar);
  const activeInk =
    contrast(palette.sidebarText, palette.sidebarActive) >= TEXT_CONTRAST
      ? palette.sidebarText
      : bestInk(palette.sidebarActive);
  return {
    "--sidebar-bg": palette.sidebar,
    "--sidebar-bg-top": mix(palette.sidebar, PAPER, sidebarIsLight ? 0.28 : 0.1),
    "--sidebar-bg-deep": mix(palette.sidebar, INK, sidebarIsLight ? 0.08 : 0.28),
    "--sidebar-fg": palette.sidebarText,
    "--sidebar-fg-muted": toneToward(palette.sidebarText, palette.sidebar, TEXT_CONTRAST),
    "--sidebar-fg-subtle": toneToward(palette.sidebarText, palette.sidebar, 3),
    "--sidebar-line": sidebarIsLight ? "rgba(11, 18, 32, 0.12)" : "rgba(255, 255, 255, 0.08)",
    "--sidebar-active": palette.sidebarActive,
    "--sidebar-active-fg": activeInk,
    "--sidebar-hover": sidebarIsLight ? "rgba(11, 18, 32, 0.06)" : "rgba(255, 255, 255, 0.08)",
  };
}

function accentVariables(palette: AppPalette): Record<string, string> {
  const pageIsLight = contrast(INK, palette.page) >= TEXT_CONTRAST;
  const hover = pageIsLight ? mix(palette.accent, INK, 0.14) : mix(palette.accent, PAPER, 0.16);
  const pressed = pageIsLight ? mix(palette.accent, INK, 0.26) : mix(palette.accent, INK, 0.12);
  const soft = mix(palette.surface, palette.accent, pageIsLight ? 0.14 : 0.32);
  return {
    "--accent-base": palette.accent,
    "--accent-hover": hover,
    "--accent-pressed": pressed,
    "--accent-soft": soft,
    "--accent-alt": palette.accent,
    "--accent-alt-hover": hover,
    "--accent-alt-soft": soft,
    "--accent-glow": rgba(palette.accent, 0.28),
    "--accent-fg": bestInk(palette.accent),
    "--focus-ring": palette.accent,
  };
}

function headerVariables(palette: AppPalette): Record<string, string> {
  const ink = sharedInk([palette.header]) ?? bestInk(palette.header);
  return {
    "--bg-header": palette.header,
    "--header-fg": ink,
    "--header-fg-muted": toneToward(ink, palette.header, TEXT_CONTRAST),
    "--header-border": mix(palette.header, ink, 0.16),
  };
}

/** Variables à poser seulement pour les groupes qui s'écartent de la palette Scola. */
export function themeOverrides(palette: AppPalette): Record<string, string> {
  const overrides: Record<string, string> = {};
  if (palette.page !== DEFAULT_PALETTE.page || palette.surface !== DEFAULT_PALETTE.surface) {
    Object.assign(overrides, surfaceVariables(palette));
  }
  if (
    palette.sidebar !== DEFAULT_PALETTE.sidebar ||
    palette.sidebarText !== DEFAULT_PALETTE.sidebarText ||
    palette.sidebarActive !== DEFAULT_PALETTE.sidebarActive
  ) {
    Object.assign(overrides, sidebarVariables(palette));
  }
  if (
    palette.accent !== DEFAULT_PALETTE.accent ||
    palette.page !== DEFAULT_PALETTE.page ||
    palette.surface !== DEFAULT_PALETTE.surface
  ) {
    Object.assign(overrides, accentVariables(palette));
  }
  if (palette.header !== DEFAULT_PALETTE.header) {
    Object.assign(overrides, headerVariables(palette));
  }
  return overrides;
}
